import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "../../api/axios";
import { formatDataGeneral } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

const REVENUE_SOURCES = [
  { name: "IGF", dataElement: "Wp7KcuZgrJa" },
  { name: "DACF", dataElement: "rtZ2oyIrEZE" },
  { name: "MPs CF", dataElement: "sPtuvxHoqBI" },
  { name: "PWDs CF", dataElement: "iPJma6G8Pen" },
  { name: "DACF-RFG", dataElement: "PnPth1bxPDM" },
  { name: "Decentralized Dept", dataElement: "PO8QzvjK8VM" },
  { name: "GOG Salaries", dataElement: "nHtXhtCsha8" },
  { name: "MDF", dataElement: "IujXTMPpFux" },
  { name: "Stool Lands", dataElement: "J8qgTRwB7wj" },
  { name: "CIDA", dataElement: "WlVIx0WUbgt" },
];

const YEAR_SUFFIXES = ["One", "Two", "Three", "Four"];

const mapData = (data, report) => {
  const attributes = data[0]?.attributes;
  const reports = report.find((rep) => rep.trackedEntity === data[0]?.trackedEntity);

  const result = REVENUE_SOURCES.map(({ name }) => {
    const estimateItem = attributes?.find(
      (item) => item?.displayName?.toLowerCase() === `${name.toLowerCase()} baseline`,
    );

    return {
      name,
      estimate: estimateItem ? Number(estimateItem.value) : 0,
      performance: 0,
    };
  });

  result.forEach((el) => {
    const source = REVENUE_SOURCES.find((item) => item.name === el.name);
    for (const r of reports?.dataValues || []) {
      if (r.dataElement === source.dataElement) {
        el.performance = Number(r.value) || 0;
        break;
      }
    }
  });

  return result;
};

function transformRevenu(data) {
  const keys = [
    "revenuThreeLessMapped",
    "revenuTwoLessMapped",
    "revenuOneLessMapped",
    "revenuFirstMapped",
  ];

  const names = data[keys[0]].map((item) => item.name);

  const result = names.map((name) => {
    const obj = { name };

    keys.forEach((key, i) => {
      const match = data[key].find((item) => item.name === name);
      if (match) {
        obj[`estimate${YEAR_SUFFIXES[i]}`] = match.estimate;
        obj[`performance${YEAR_SUFFIXES[i]}`] = match.performance;
      }
    });

    return obj;
  });

  const totalRow = { name: "Total" };

  YEAR_SUFFIXES.forEach((suffix) => {
    totalRow[`estimate${suffix}`] = result.reduce(
      (sum, el) => sum + (el[`estimate${suffix}`] || 0),
      0,
    );
    totalRow[`performance${suffix}`] = result.reduce(
      (sum, el) => sum + (el[`performance${suffix}`] || 0),
      0,
    );
  });

  result.push(totalRow);
  return result;
}

const Table_9 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  const years = useMemo(
    () => [selectedYear - 3, selectedYear - 2, selectedYear - 1, selectedYear],
    [selectedYear],
  );

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_9",
      tableData,
    }),
  }));

  function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    const startDate = `${years[0]}-01-01`;
    const endDate = `${years[3]}-12-31`;

    axios
      .get(`/tracker/trackedEntities?orgUnit=${district}&program=SY8TpfPgzr9&startDate=${startDate}&endDate=${endDate}`)
      .then((result) => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=SY8TpfPgzr9&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}`)
            .then((resp) => {
              const revenues = result.data.instances;
              const reports = resp.data.instances;

              const revenuFirst = formatDataGeneral(revenues, "Years", `${selectedYear}`) || [];
              const revenuOneLess = formatDataGeneral(revenues, "Years", `${selectedYear - 1}`) || [];
              const revenuTwoLess = formatDataGeneral(revenues, "Years", `${selectedYear - 2}`) || [];
              const revenuThreeLess = formatDataGeneral(revenues, "Years", `${selectedYear - 3}`) || [];

              const revenuFirstMapped = mapData(revenuFirst, reports);
              const revenuOneLessMapped = mapData(revenuOneLess, reports);
              const revenuTwoLessMapped = mapData(revenuTwoLess, reports);
              const revenuThreeLessMapped = mapData(revenuThreeLess, reports);

              const tempResult = {
                revenuFirstMapped,
                revenuOneLessMapped,
                revenuTwoLessMapped,
                revenuThreeLessMapped,
              };

              setTableData(transformRevenu(tempResult));
            })
            .catch((err) => console.log(err));
        } else {
          setTableData([]);
        }
      })
      .catch((err) => console.log(err));
  }

  const formatCell = (value) => (value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="col-12">
      <h3>Table 9: Update on Revenue Sources as of the end of the Year (current year and last 3 previous years)</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table9-${year}`}
            hideTableDis={hideTableDis}
          />
          <div className="table-responsive">
            <h6>The funding sources of the Assembly, over the years have been the Central Government
              transfers to MMDAs (GOG Grants), the District Assembly Common Fund (DACF), the
              District Assembly Common Fund-Responsive Factor Grant (DACF-RFG), the Minerals
              Development Fund, Donor Grants and the Assembly’s own Internally Generated Funds
              (IGF). Others are the Member of Parliament’s Common Fund (MP’sCF).</h6>
            <h7>
              During the year under review, funds received included the Internally Generated Funds;
              District Assembly’s Common Fund; Persons with Disability Common Fund; Member of
              Parliament’s Common Fund and the District Assemblies Common Fund-Responsive Factor
              Grant. Table 2.3 shows the updates from the various sources and their targets.</h7>

            <table
              className="table table-bordered"
              style={{
                marginTop: 20,
                border: "1px solid #000",
                borderCollapse: "collapse",
                width: "100%",
              }}
            >
              <thead
                style={{
                  backgroundColor: "#d4edda",
                  fontWeight: "bold",
                }}
              >
                <tr>
                  <th style={{ border: "1px solid #000" }} rowSpan="2">Revenue Source</th>
                  <th style={{ border: "1px solid #000" }} colSpan="4">Estimates</th>
                  <th style={{ border: "1px solid #000" }} colSpan="4">Performance</th>
                </tr>
                <tr>
                  {years.map((y) => (
                    <th key={`est-${y}`} style={{ border: "1px solid #000" }}>{y}</th>
                  ))}
                  {years.map((y) => (
                    <th key={`perf-${y}`} style={{ border: "1px solid #000" }}>{y}</th>
                  ))}
                </tr>
              </thead>
              {tableData.length > 0 && (
                <tbody>
                  {tableData.map((row, index) => (
                    <tr
                      key={index}
                      style={row.name === "Total" ? { fontWeight: "bold", backgroundColor: "#f0f0f0" } : undefined}
                    >
                      <td style={{ border: "1px solid #000" }}>{row.name}</td>
                      {YEAR_SUFFIXES.map((suffix) => (
                        <td key={`est-${suffix}`} style={{ border: "1px solid #000" }}>
                          {formatCell(row[`estimate${suffix}`])}
                        </td>
                      ))}
                      {YEAR_SUFFIXES.map((suffix) => (
                        <td key={`perf-${suffix}`} style={{ border: "1px solid #000" }}>
                          {formatCell(row[`performance${suffix}`])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table9-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_9;
